import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { SchoolSales, VatCompany, VatInvoice } from "lib/models";
import { ClassNames } from "lib/types";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import VatCompanyPopup from "../Main/VatCompanyPopup";
import DateTimePicker from "components/Common/datetime-picker";
import { NumericFormat } from "react-number-format";
import { safeParseInt } from "components/Common/Util";
import toast from "react-hot-toast";
import { apiPost } from "components/Common/Axios";
import { xor } from "lib/utils";
import { useAlert } from "components/ui/alert-context";
import dayjs from "dayjs";
import { Dialog, DialogContent } from "components/ui/dialog";

export type IssuedType = "new" | "total" | "lecture" | "material";

const VatIssueRequestPopup = ({
  open,
  onOpenChange,
  schoolSalesList,
  setRequestSchoolSales,
  isEditMode,
  reload,
  issuedType,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  schoolSalesList: SchoolSales[];
  setRequestSchoolSales: (schoolSalesList: SchoolSales[]) => void;
  isEditMode: boolean;
  reload: () => void;
  issuedType: IssuedType;
}) => {
  const [vatInvoice, setVatInvoice] = useState<VatInvoice>({
    id: 0,
    vatCompanyId: 0,
    supplyDate1: new Date(),
    totalAmt1: 0,
    supplyAmt1: 0,
    taxAmt1: 0,
    supplyContent1: "",
    supplyDate2: new Date(),
    totalAmt2: 0,
    supplyAmt2: 0,
    taxAmt2: 0,
    supplyContent2: "",
    supplyDate3: new Date(),
    totalAmt3: 0,
    supplyAmt3: 0,
    taxAmt3: 0,
    supplyContent3: "",
    supplyDate4: new Date(),
    totalAmt4: 0,
    supplyAmt4: 0,
    taxAmt4: 0,
    supplyContent4: "",
    totalAmt: 0,
    supplyAmt: 0,
    taxAmt: 0,
    supplyContent: "",
    depositAmt: 0,
    isIssuedCompleted: false,
    issuedDate: undefined,
    isDepositCompleted: false,
    depositDate: undefined,
  } as VatInvoice);
  const [openVatPopup, setOpenVatPopup] = useState(false);
  const [checkRevenues, setCheckRevenues] = useState<
    {
      isCheckLecture: boolean;
      isCheckMaterial: boolean;
      isShowLecture: boolean;
      isShowMaterial: boolean;
      isEnableLecture: boolean;
      isEnableMaterial: boolean;
    }[]
  >([]);

  const [supplyDates, setSupplyDates] = useState<Date[]>([
    new Date(),
    new Date(),
    new Date(),
    new Date(),
  ]);
  const [totalAmts, setTotalAmts] = useState<number[]>([0, 0, 0, 0]);
  const [supplyAmts, setSupplyAmts] = useState<number[]>([0, 0, 0, 0]);
  const [taxAmts, setTaxAmts] = useState<number[]>([0, 0, 0, 0]);
  const [supplyContents, setSupplyContents] = useState<string[]>([
    "",
    "",
    "",
    "",
  ]);

  const { show } = useAlert();

  useEffect(() => {
    if (!open) {
      clearInput();
    }
  }, [open]);

  useEffect(() => {
    setSupplyDates([
      vatInvoice.supplyDate1,
      vatInvoice.supplyDate2,
      vatInvoice.supplyDate3,
      vatInvoice.supplyDate4,
    ]);
    setTotalAmts([
      vatInvoice.totalAmt -
        vatInvoice.totalAmt2 -
        vatInvoice.totalAmt3 -
        vatInvoice.totalAmt4,
      vatInvoice.totalAmt2,
      vatInvoice.totalAmt3,
      vatInvoice.totalAmt4,
    ]);

    setSupplyAmts([
      vatInvoice.supplyAmt1,
      vatInvoice.supplyAmt2,
      vatInvoice.supplyAmt3,
      vatInvoice.supplyAmt4,
    ]);
    setTaxAmts([
      vatInvoice.taxAmt1,
      vatInvoice.taxAmt2,
      vatInvoice.taxAmt3,
      vatInvoice.taxAmt4,
    ]);
    setSupplyContents([
      vatInvoice.supplyContent1,
      vatInvoice.supplyContent2,
      vatInvoice.supplyContent3,
      vatInvoice.supplyContent4,
    ]);
  }, [vatInvoice]);

  // 여기서 Poup의 Check상태가 결정된다
  useEffect(() => {
    setCheckRevenues(
      schoolSalesList.map((schoolSales) => {
        let isCheckLecture = false;
        let isCheckMaterial = false;
        let isShowLecture = true;
        let isShowMaterial = true;
        let isEnableLecture = true;
        let isEnableMaterial = true;

        if (schoolSales.revenueMaterial < 1) {
          isShowMaterial = false;
        }

        if (issuedType === "new") {
          if (schoolSales.isIssuedLecture) {
            isCheckLecture = true;
            isEnableLecture = false;
          }
          if (schoolSales.isIssuedMaterial) {
            isCheckMaterial = true;
            isEnableMaterial = false;
          }
        } else if (issuedType === "lecture") {
          isCheckLecture = true;
          isEnableLecture = false;

          isShowMaterial = false;
        } else if (issuedType === "material") {
          isCheckMaterial = true;
          isEnableMaterial = false;

          isShowLecture = false;
        } else if (issuedType === "total") {
          isCheckLecture = true;
          isEnableLecture = false;
          isCheckMaterial = true;
          isEnableMaterial = false;
        }

        return {
          isCheckLecture,
          isCheckMaterial,
          isShowLecture,
          isShowMaterial,
          isEnableLecture,
          isEnableMaterial,
        };
      })
    );

    if (schoolSalesList.length < 1) {
      return;
    }

    if (issuedType === "new") {
      setVatInvoice(vatInvoice);
    } else if (issuedType === "lecture" || issuedType === "total") {
      setVatInvoice(schoolSalesList[0].vatInvoice ?? vatInvoice);
    } else {
      setVatInvoice(schoolSalesList[0].vatInvoiceMaterial ?? vatInvoice);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [issuedType, schoolSalesList]);

  useEffect(() => {
    calcSupplyTaxAmts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalAmts]);

  // 강사비, 재료비 체크박스 클릭시
  const handleCheckRevenues = (
    index: number,
    isUseLecture: boolean,
    checked: boolean
  ) => {
    const newRevenues = [...checkRevenues];
    newRevenues[index] = {
      ...newRevenues[index],
      isCheckLecture: isUseLecture
        ? checked
        : newRevenues[index].isCheckLecture,
      isCheckMaterial: !isUseLecture
        ? checked
        : newRevenues[index].isCheckMaterial,
    };

    const totalAmtLecture = schoolSalesList
      .filter(
        (schoolSales, i) =>
          newRevenues[i].isCheckLecture && newRevenues[i].isEnableLecture
      )
      .reduce((acc, cur) => acc + cur.revenueLecture, 0);

    const totalAmtMaterial = schoolSalesList
      .filter(
        (schoolSales, i) =>
          newRevenues[i].isCheckMaterial && newRevenues[i].isEnableMaterial
      )
      .reduce((acc, cur) => acc + cur.revenueMaterial, 0);

    const totalAmt = totalAmtLecture + totalAmtMaterial;

    const supplyAmt = Math.round(totalAmt / 1.1);
    const taxAmt = totalAmt - supplyAmt;

    setVatInvoice({
      ...vatInvoice,
      totalAmt,
      supplyAmt,
      taxAmt,
    });
    setCheckRevenues(newRevenues);
  };

  // VAT Company 선택 (...) 클릭
  const handleSelectVatCompany = (isLecture: boolean) => {
    setOpenVatPopup(true);
  };

  // 팝업에서 VAT Company 선택시
  const onSelectVatCompany = (vatCompany: VatCompany) => {
    setVatInvoice({
      ...vatInvoice,
      vatCompanyId: vatCompany.id,
      vatCompany,
    });
    setOpenVatPopup(false);
  };

  // 4개의 총액 공급가액 세액 변경
  const calcSupplyTaxAmts = () => {
    const newSupplyAmts = [...supplyAmts];
    const newTaxAmts = [...taxAmts];

    totalAmts.forEach((totalAmt, i) => {
      newSupplyAmts[i] = Math.round(totalAmt / 1.1);
      newTaxAmts[i] = totalAmt - newSupplyAmts[i];
    });
    setSupplyAmts(newSupplyAmts);
    setTaxAmts(newTaxAmts);
  };

  // 4개의 날짜가 변경시
  const handleDetailDateChange = (i: number, date: Date) => {
    const newSupplyDates = supplyDates.map((d, j) => (i === j ? date : d));
    setSupplyDates(newSupplyDates);

    vatInvoice.supplyDate1 = newSupplyDates[0];
    vatInvoice.supplyDate2 = newSupplyDates[1];
    vatInvoice.supplyDate3 = newSupplyDates[2];
    vatInvoice.supplyDate4 = newSupplyDates[3];
  };

  // 4개의 총액이 변경시
  const handleDetailTotalAmtChange = (i: number, amt: number) => {
    if (i === 0) {
      return;
    }

    const newTotalAmts = [...totalAmts];
    newTotalAmts[i] = amt;
    newTotalAmts[0] =
      vatInvoice.totalAmt - newTotalAmts[1] - newTotalAmts[2] - newTotalAmts[3];
    setTotalAmts(newTotalAmts);

    vatInvoice.totalAmt1 = newTotalAmts[0];
    vatInvoice.totalAmt2 = newTotalAmts[1];
    vatInvoice.totalAmt3 = newTotalAmts[2];
    vatInvoice.totalAmt4 = newTotalAmts[3];
  };

  // 저장
  const handleSave = async () => {
    if (!vatInvoice.vatCompanyId) {
      toast.error("발급처를 선택해주세요.");
      return;
    }
    if (vatInvoice.totalAmt === 0) {
      toast.error("총액이 0입니다. 적용할 금액을 체크해주세요");
      return;
    }
    for (let i = 0; i < 4; i++) {
      if (totalAmts[i] > 0 && supplyContents[i] === "") {
        toast.error(`${i + 1}번째 공급내용을 입력해주세요`);
        return;
      }
    }

    try {
      vatInvoice.supplyDate1 = supplyDates[0];
      vatInvoice.supplyDate2 = supplyDates[1];
      vatInvoice.supplyDate3 = supplyDates[2];
      vatInvoice.supplyDate4 = supplyDates[3];
      vatInvoice.totalAmt1 = totalAmts[0];
      vatInvoice.totalAmt2 = totalAmts[1];
      vatInvoice.totalAmt3 = totalAmts[2];
      vatInvoice.totalAmt4 = totalAmts[3];
      vatInvoice.supplyAmt1 = supplyAmts[0];
      vatInvoice.supplyAmt2 = supplyAmts[1];
      vatInvoice.supplyAmt3 = supplyAmts[2];
      vatInvoice.supplyAmt4 = supplyAmts[3];
      vatInvoice.taxAmt1 = taxAmts[0];
      vatInvoice.taxAmt2 = taxAmts[1];
      vatInvoice.taxAmt3 = taxAmts[2];
      vatInvoice.taxAmt4 = taxAmts[3];
      vatInvoice.supplyContent1 = supplyContents[0];
      vatInvoice.supplyContent2 = supplyContents[1];
      vatInvoice.supplyContent3 = supplyContents[2];
      vatInvoice.supplyContent4 = supplyContents[3];
      vatInvoice.supplyAmt =
        supplyAmts[0] + supplyAmts[1] + supplyAmts[2] + supplyAmts[3];
      vatInvoice.taxAmt = taxAmts[0] + taxAmts[1] + taxAmts[2] + taxAmts[3];

      const {
        vatCompany,
        schoolSales,
        schoolSalesMaterial,
        ...vatInvoiceSave
      } = vatInvoice;

      const response = await apiPost("/vat-invoice", vatInvoiceSave);
      if (response.error) {
        toast.error("저장 실패했습니다");
        console.error("handleSave.error", response.error);
        return;
      }

      const invoice = response.data as VatInvoice;

      if (issuedType === "new") {
        checkRevenues.forEach((checkRevenue, i) => {
          // 강사비가 Enable상태에서 체크되어있으면 강사비 발급완료
          if (
            checkRevenue.isShowLecture &&
            checkRevenue.isEnableLecture &&
            checkRevenue.isCheckLecture
          ) {
            schoolSalesList[i].isIssuedLecture = true;
            schoolSalesList[i].vatInvoiceId = invoice.id;
          }

          // 재료비가 Enable상태에서 체크되어있으면 재료비 발급완료
          if (
            checkRevenue.isShowMaterial &&
            checkRevenue.isEnableMaterial &&
            checkRevenue.isCheckMaterial
          ) {
            schoolSalesList[i].isIssuedMaterial = true;
            schoolSalesList[i].vatInvoiceMaterialId = invoice.id;
          }

          // 강사비가 안보이거나(0) 발급되었고 재료비가 안보이거나(0) 발급되었으면 발급완료(isVatInvoiceRequested) = true
          schoolSalesList[i].isVatInvoiceRequested =
            (!checkRevenue.isShowLecture || checkRevenue.isCheckLecture) &&
            (!checkRevenue.isShowMaterial || checkRevenue.isCheckMaterial);

          // 강사비와 재료비중 하나만 발급되었으며 둘다 0이 아니거나,
          // 둘다 발급되고 각각의 invoideId가 다르면 분리발급(isIssuedSeparate) = true
          schoolSalesList[i].isIssuedSeparate =
            (xor(checkRevenue.isCheckLecture, checkRevenue.isCheckMaterial) &&
              schoolSalesList[i].revenueLecture !== 0 &&
              schoolSalesList[i].revenueMaterial !== 0) ||
            (checkRevenue.isCheckLecture &&
              checkRevenue.isCheckMaterial &&
              schoolSalesList[i].vatInvoiceId !==
                schoolSalesList[i].vatInvoiceMaterialId);
        });
      }

      const response2 = await apiPost(
        "/school-sales/update-invoice",
        schoolSalesList
      );
      if (response2.error) {
        toast.error("저장 실패했습니다");
        console.error("handleSave.error", response2.error);
        return;
      }
      toast.success("저장했습니다");
      onOpenChange(false);
      reload();
    } catch (error: any) {
      toast.error("저장 실패했습니다");
      console.error("handleSave.error", error);
      return;
    }
  };

  // 발급취소
  const handleCancelIssue = async () => {
    show("신청 취소", "발급신청을 취소하시겠습니까?", "confirm", async () => {
      try {
        const response = await apiPost("/school-sales/cancel-invoice", {
          schoolSalesList,
          vatInvoice,
        });
        if (response.error) {
          toast.error("취소 실패했습니다");
          console.error("handleCancelIssue.error", response.error);
          return;
        }
        toast.success("취소했습니다");
        onOpenChange(false);
        reload();
      } catch (error: any) {
        toast.error("취소 실패했습니다");
        console.error("handleCancelIssue.error", error);
        return;
      }
    });
  };

  // 취소
  const handleCancle = () => {
    onOpenChange(false);
  };

  const clearInput = () => {
    setVatInvoice({
      id: 0,
      vatCompanyId: 0,
      supplyDate1: new Date(),
      totalAmt1: 0,
      supplyAmt1: 0,
      taxAmt1: 0,
      supplyContent1: "",
      supplyDate2: new Date(),
      totalAmt2: 0,
      supplyAmt2: 0,
      taxAmt2: 0,
      supplyContent2: "",
      supplyDate3: new Date(),
      totalAmt3: 0,
      supplyAmt3: 0,
      taxAmt3: 0,
      supplyContent3: "",
      supplyDate4: new Date(),
      totalAmt4: 0,
      supplyAmt4: 0,
      taxAmt4: 0,
      supplyContent4: "",
      totalAmt: 0,
      supplyAmt: 0,
      taxAmt: 0,
      supplyContent: "",
      depositAmt: 0,
      isIssuedCompleted: false,
      issuedDate: undefined,
      isDepositCompleted: false,
      depositDate: undefined,
    } as VatInvoice);
    setTotalAmts([0, 0, 0, 0]);
    setSupplyDates([new Date(), new Date(), new Date(), new Date()]);
    setSupplyContents(["", "", "", ""]);
  };

  if (schoolSalesList.length !== checkRevenues.length) {
    return null;
  }

  return (
    <>
      <VatCompanyPopup
        open={openVatPopup}
        onOpenChange={setOpenVatPopup}
        currentVatCompanyId={vatInvoice.vatCompanyId}
        onSelectVatCompany={onSelectVatCompany}
        popupLevel={1}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-lg max-h-[95%] overflow-x-hidden overflow-y-auto thin-scrollbar pb-0">
          <Card className="border-0 shadow-none">
            <CardHeader className="px-0 pt-0 pb-3 mb-1 sm:px-2 border-b border-gray-100">
              <CardTitle className="text-xl sm:text-2xl font-bold text-center text-gray-900">
                세금계산서 발급 {isEditMode ? "수정" : "신청"}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 sm:px-2 flex flex-col gap-3 text-sm">
              <div className="overflow-x-auto"><div className="min-w-[760px] flex flex-col gap-3">
              <div className="grid grid-cols-[1fr_1fr_1fr_70px_90px_120px_120px_1fr] items-center gap-x-5 border-b border-solid border-gray-200 pb-3">
                <div className="text-center">학교명</div>
                <div className="text-center">수업일시</div>
                <div className="text-center">과목</div>
                <div className="text-center">교시</div>
                <div className="text-center">학급수</div>
                <div className="text-right">강사비</div>
                <div className="text-right">재료비</div>
                <div className="text-right">합계</div>
              </div>
              {schoolSalesList.map((schoolSales, index) => (
                <div
                  key={schoolSales.id}
                  className="grid grid-cols-[1fr_1fr_1fr_70px_90px_120px_120px_1fr] items-center gap-x-5 border-b border-solid border-gray-300 pb-3"
                >
                  <div>{schoolSales.school.name}</div>
                  <div className="text-center">
                    {dayjs(schoolSales.classDateFrom)
                      .toDate()
                      .toLocaleDateString()}
                  </div>
                  <div className="text-center">
                    {ClassNames[schoolSales.classType - 1]}
                  </div>
                  <div className="text-center">{schoolSales.classTimes}</div>
                  <div className="text-center">{schoolSales.contractCnt}</div>
                  {/* 강사비 */}
                  <div className="flex justify-end items-center gap-2">
                    <div>{schoolSales.revenueLecture.toLocaleString()}</div>
                    {checkRevenues[index].isShowLecture && (
                      <Checkbox
                        className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 data-[state=disabled]:border-gray-300"
                        checked={checkRevenues[index].isCheckLecture}
                        onCheckedChange={(checked: boolean) =>
                          handleCheckRevenues(index, true, checked)
                        }
                        disabled={!checkRevenues[index].isEnableLecture}
                      />
                    )}
                  </div>
                  {/* 재료비 */}
                  <div className="flex justify-end items-center gap-2">
                    {schoolSales.revenueMaterial.toLocaleString()}
                    {checkRevenues[index].isShowMaterial && (
                      <Checkbox
                        className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                        checked={checkRevenues[index].isCheckMaterial}
                        onCheckedChange={(checked: boolean) =>
                          handleCheckRevenues(index, false, checked)
                        }
                        disabled={!checkRevenues[index].isEnableMaterial}
                      />
                    )}
                  </div>
                  <div className="text-right">
                    {schoolSales.revenue.toLocaleString()}
                  </div>
                </div>
              ))}
              </div></div>

              <div className="mt-5 p-3 md:py-5 md:px-4 md:mx-20 flex flex-col gap-2 border border-gray-200 border-solid">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-full sm:w-[200px] text-left sm:text-right">세금계산서 발급처</div>
                  <div className="w-full sm:w-[300px] flex items-center gap-1">
                    <Input
                      value={vatInvoice.vatCompany?.nickName ?? ""}
                      className="border border-solid border-gray-300 w-full"
                      readOnly
                      onChange={() => {}}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSelectVatCompany(false)}
                    >
                      ...
                    </Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-full sm:w-[200px] text-left sm:text-right">발급일</div>
                  <div className="w-full sm:w-[200px] flex items-center gap-1">
                    <DateTimePicker
                      datetime={vatInvoice.issuedDate ?? new Date()}
                      setDatetime={(date) =>
                        setVatInvoice({ ...vatInvoice, issuedDate: date })
                      }
                      className="w-[200px]"
                      isShowTime={false}
                    />
                  </div>
                  <div className="w-full sm:w-[80px] text-left sm:text-right">총액</div>
                  <div className="w-full sm:w-[200px] flex items-center gap-1">
                    <NumericFormat
                      value={vatInvoice.totalAmt}
                      thousandSeparator=","
                      allowLeadingZeros={false}
                      readOnly
                      className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-2 w-full text-gray-500"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-full sm:w-[200px] text-left sm:text-right">공급가액</div>
                  <div className="w-full sm:w-[200px] flex items-center gap-1">
                    <NumericFormat
                      value={
                        supplyAmts[0] +
                        supplyAmts[1] +
                        supplyAmts[2] +
                        supplyAmts[3]
                      }
                      thousandSeparator=","
                      allowLeadingZeros={false}
                      readOnly
                      className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-2 w-full text-gray-500"
                    />
                  </div>
                  <div className="w-full sm:w-[80px] text-left sm:text-right">세액</div>
                  <div className="w-full sm:w-[200px] flex items-center gap-1">
                    <NumericFormat
                      value={taxAmts[0] + taxAmts[1] + taxAmts[2] + taxAmts[3]}
                      thousandSeparator=","
                      allowLeadingZeros={false}
                      readOnly
                      className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-2 w-full text-gray-500"
                    />
                  </div>
                </div>
              </div>
              <div className="px-0 md:px-20 pt-10 overflow-x-auto"><div className="min-w-[640px]">
                <div className="grid grid-cols-[150px_120px_120px_120px_1fr] items-center gap-x-2 border-b border-solid border-gray-200 pb-3">
                  <div className="text-center">공급일</div>
                  <div className="text-center">총액</div>
                  <div className="text-center">공급가액</div>
                  <div className="text-center">세액</div>
                  <div className="text-center">내용</div>
                </div>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[150px_120px_120px_120px_1fr] items-center gap-x-2 border-b border-solid border-gray-200 py-2"
                  >
                    <div className="text-center">
                      <DateTimePicker
                        datetime={supplyDates[i]}
                        setDatetime={(date) => {
                          // setVatInvoice({
                          //   ...vatInvoice,
                          //   [i === 0
                          //     ? "supplyDate1"
                          //     : i === 1
                          //     ? "supplyDate2"
                          //     : i === 2
                          //     ? "supplyDate3"
                          //     : "supplyDate4"]: date,
                          // });
                          handleDetailDateChange(i, date);

                          // setSupplyDates(
                          //   supplyDates.map((d, j) => (i === j ? date : d))
                          // );
                        }}
                        isShowTime={false}
                      />
                    </div>
                    <div className="text-center">
                      <NumericFormat
                        value={totalAmts[i]}
                        thousandSeparator=","
                        allowLeadingZeros={false}
                        readOnly={i === 0}
                        onChange={(e) => {
                          handleDetailTotalAmtChange(
                            i,
                            safeParseInt(e.target.value)
                          );
                        }}
                        className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-2 w-full"
                      />
                    </div>
                    <div className="text-center">
                      <NumericFormat
                        value={supplyAmts[i]}
                        thousandSeparator=","
                        allowLeadingZeros={false}
                        readOnly
                        className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-2 w-full text-gray-400"
                      />
                    </div>
                    <div className="text-center">
                      <NumericFormat
                        value={taxAmts[i]}
                        thousandSeparator=","
                        allowLeadingZeros={false}
                        readOnly
                        className="text-right border border-solid border-gray-300 rounded-lg p-2 pr-2 w-full text-gray-400"
                      />
                    </div>
                    <div className="text-center">
                      <Input
                        value={supplyContents[i]}
                        className="border border-solid border-gray-300 w-full text-left"
                        onChange={(e) => {
                          setVatInvoice({
                            ...vatInvoice,
                            [i === 0
                              ? "supplyContent1"
                              : i === 1
                              ? "supplyContent2"
                              : i === 2
                              ? "supplyContent3"
                              : "supplyContent4"]: e.target.value,
                          });
                          setSupplyContents(
                            supplyContents.map((c, j) =>
                              i === j ? e.target.value : c
                            )
                          );
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              </div>
              <div className="mt-5 pt-5 flex flex-wrap justify-between gap-2 border-t border-gray-200 border-solid">
                <div>
                  {issuedType !== "new" && (
                    <Button
                      size="lg"
                      variant="destructive"
                      onClick={() => handleCancelIssue()}
                    >
                      발급신청 취소
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="lg"
                    variant="primary"
                    onClick={() => handleSave()}
                  >
                    {issuedType === "new" ? "발급신청" : "수정"}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={() => handleCancle()}
                  >
                    취소
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VatIssueRequestPopup;
